import { useState, useCallback, useRef } from 'react'
import { Upload, X, FileText, AlertCircle, CheckCircle } from 'lucide-react'
import { uploadApi, FileUploadResult } from '../../services/api'

const SUPPORTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md', '.jpg', '.jpeg', '.png', '.tiff', '.tif']
const MAX_FILE_SIZE = 500 * 1024 * 1024 // 500 MB

interface FileUploadProps {
  onUploadComplete: (results: FileUploadResult[]) => void
  useOcr?: boolean
  onUseOcrChange?: (useOcr: boolean) => void
}

export default function FileUpload({
  onUploadComplete,
  useOcr = true,
  onUseOcrChange,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadResults, setUploadResults] = useState<FileUploadResult[]>([])
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      return `Nieobsługiwany format: ${ext}`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `Plik zbyt duży: ${(file.size / 1024 / 1024).toFixed(1)} MB (max 500 MB)`
    }
    return null
  }

  const handleFiles = useCallback((files: FileList | File[]) => {
    setError(null)
    const fileArray = Array.from(files)
    const validFiles: File[] = []
    const errors: string[] = []

    fileArray.forEach(file => {
      const validationError = validateFile(file)
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`)
      } else {
        validFiles.push(file)
      }
    })

    if (errors.length > 0) {
      setError(errors.join('\n'))
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles])
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files)
    }
  }, [handleFiles])

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return

    setIsUploading(true)
    setError(null)

    try {
      const response = await uploadApi.uploadFiles(selectedFiles, useOcr)
      setUploadResults(response.data.results)
      onUploadComplete(response.data.results)
      setSelectedFiles([])
    } catch (err) {
      setError('Wystąpił błąd podczas przesyłania plików')
      console.error('Upload error:', err)
    } finally {
      setIsUploading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
      case 'empty':
      case 'skipped':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      default:
        return <FileText className="w-4 h-4 text-gray-400" />
    }
  }

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={SUPPORTED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
        <p className="text-sm text-gray-600">
          Przeciągnij plik tutaj lub <span className="text-blue-600">kliknij, aby wybrać</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          PDF, DOC, DOCX, TXT, MD, obrazy (max 500 MB)
        </p>
      </div>

      {/* OCR toggle */}
      {onUseOcrChange && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={useOcr}
            onChange={(e) => onUseOcrChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-gray-700">Użyj OCR dla skanów i obrazów</span>
        </label>
      )}

      {/* Selected files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Wybrane pliki:</p>
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between p-2 bg-gray-50 rounded"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{file.name}</span>
                <span className="text-xs text-gray-400">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className="btn-primary w-full"
          >
            {isUploading ? 'Przesyłanie...' : 'Prześlij pliki'}
          </button>
        </div>
      )}

      {/* Upload results */}
      {uploadResults.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Wyniki:</p>
          {uploadResults.map((result, index) => (
            <div
              key={`${result.filename}-${index}`}
              className="flex items-center justify-between p-2 bg-gray-50 rounded"
            >
              <div className="flex items-center gap-2">
                {getStatusIcon(result.status)}
                <span className="text-sm text-gray-700">{result.filename}</span>
                {result.status === 'success' && (
                  <span className="text-xs text-gray-400">
                    ({result.textLength} znaków{result.ocrUsed ? ', OCR' : ''})
                  </span>
                )}
              </div>
              {result.error && (
                <span className="text-xs text-red-500">{result.error}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  )
}
